import type { LocalAnnotationStore } from './annotations.ts'
import type { LocalFriendStore } from './friends.ts'
import type { Annotation, Friend, SharedAnnotation } from './types.ts'

/**
 * Keeps the local store and the cloud in step.
 *
 * The local store stays authoritative for reads (KTD16). Sign-in adds mirroring; it
 * never swaps the read path. A signed-in user opening the app in a dead zone must
 * still see their own notes — that is the condition this app is actually used in.
 *
 * The Firestore specifics live behind `RemoteAnnotations` so this logic can be
 * tested without a network or an emulator.
 */

/**
 * One of this user's own documents, as the cloud currently holds it.
 *
 * `readers` rides alongside rather than on the `Annotation`, because the local record
 * deliberately has no such field: it is a grant this client composes per upload from
 * the live friend list, never something it reads back and republishes. But the *stamp*
 * has to record what the cloud actually holds — stamping it with what this device
 * would publish makes `pushLocal` skip a document whose grant is still narrow, and the
 * friend silently never sees it.
 */
export interface OwnRow {
  annotation: Annotation
  readers: string[]
}

export interface RemoteAnnotations {
  /**
   * Write one annotation, with the reader set it should be published to.
   *
   * `readers` is a separate argument rather than a field on the annotation because it
   * is not part of the local record at all: it is composed per upload from the live
   * friend list, and the caller — not the stored document — is the authority on who
   * may read it (KTD3). Called for every local change.
   */
  upsert(annotation: Annotation, readers: readonly string[]): Promise<void>
  /** Live query: annotations owned by this user. */
  subscribeOwn(uid: string, onChange: (list: OwnRow[]) => void): () => void
  /** Live query: annotations other people have shared with this user. */
  subscribeShared(uid: string, onChange: (list: SharedAnnotation[]) => void): () => void
  /**
   * Live query: annotations a friend has made this user a reader of (R12, KTD1).
   *
   * The third of the three constant listeners KTD2 keeps: `uid == me`, `sharedWith
   * array-contains me`, and this one. It is what puts a friend's notes on every concert
   * they annotated rather than only the ones they explicitly shared.
   */
  subscribeVisible(uid: string, onChange: (list: SharedAnnotation[]) => void): () => void
  /**
   * Live view of this user's friend list — friends and requests both ways.
   *
   * Here rather than beside sign-in because the sync layer is the only thing that needs
   * it: the friend list decides `readers` on every upload, so a change to it is a reason
   * to re-publish, and that makes it part of the same lifetime as the annotation
   * listeners rather than of the Firebase bundle.
   */
  subscribeFriendList(uid: string, onChange: (friends: Friend[]) => void): () => void
  /** Remove one annotation. Used to propagate a local clear. */
  remove(uid: string, concertId: string): Promise<void>
  /** Remove every annotation owned by this user. */
  deleteAllOwnedBy(uid: string): Promise<void>
}

/**
 * The reader set for one person's annotation: the owner, then their accepted friends.
 *
 * The owner comes first and is never repeated, which is also what `readersFor` in
 * `server/friends.js` produces — the fan-out and the client must agree on the array, or
 * every write would look like a change to the other. Duplicates are dropped because
 * `firestore.rules` caps the array at 26 and a repeat would spend one of those slots on
 * nobody; the owner's own uid appearing in the friend list — a self-friendship the
 * server should never write — is skipped for the same reason, rather than rendering the
 * reader's own notes back at them as a friend's.
 *
 * The argument is `acceptedUids()`, never `friends()`. A pending request in `readers`
 * would grant read access the other party never agreed to, and the server keeps
 * requests out of `uids` precisely so this cannot happen (KTD4, R29).
 */
export function readersFor(ownerUid: string, acceptedUids: readonly string[]): string[] {
  const readers = [ownerUid]
  for (const uid of acceptedUids) {
    if (uid && uid !== ownerUid && !readers.includes(uid)) readers.push(uid)
  }
  return readers
}

/**
 * What was actually published for one annotation, as a comparable key.
 *
 * `updatedAt` alone is not enough. `startSync` runs its first push before any friend
 * list can have arrived, so those uploads carry `readers: [me]` — which the rules
 * *accept*, because a subset of the friend list is legal. Keyed on the timestamp only,
 * the snapshot that follows would skip every id already sent, and notes written before
 * the list loaded would stay invisible to friends until the user happened to edit each
 * one. Folding the published reader set into the key makes the widened list a change
 * worth re-uploading. (The opposite case — a stale list naming a removed friend — is
 * rejected outright and self-heals through `pushed.delete` on the error path.)
 *
 * Joined on a character neither a timestamp nor a uid can contain, as `sharedKey` is,
 * so no two different publications can collapse into the same stamp.
 */
function pushStamp(updatedAt: string, readers: readonly string[]): string {
  return `${updatedAt}\u0000${readers.join('\u0000')}`
}

export interface SyncHandle {
  stop(): void
  /** Resolves once the initial local-to-cloud push has been attempted. */
  ready: Promise<void>
}

export function startSync(
  store: LocalAnnotationStore,
  friends: LocalFriendStore,
  remote: RemoteAnnotations,
  uid: string,
  ownerName: string | null,
  onError: (error: unknown) => void = () => {},
): SyncHandle {
  let stopped = false
  // Guards the write-back loop: applying a remote change notifies local
  // subscribers, which would otherwise immediately push the same value back.
  let applyingRemote = false

  // Annotations written before sign-in belong to this user now.
  store.setOwner(uid, ownerName)

  const pushed = new Map<string, string>()

  /**
   * Who this device would publish to right now.
   *
   * Read from the store on every use rather than captured once: the friend list arrives
   * asynchronously and changes while the app is open, and a captured copy is exactly the
   * stale grant `readersFor` exists to prevent.
   */
  function currentReaders(): string[] {
    return readersFor(uid, friends.acceptedUids())
  }

  async function pushLocal() {
    const readers = currentReaders()

    // Deletions first: an annotation that was cleared locally must be removed
    // remotely, or the next snapshot merges the stale row straight back in.
    for (const concertId of store.pendingDeletions()) {
      try {
        await remote.remove(uid, concertId)
        store.clearTombstone(concertId)
        pushed.delete(concertId)
      } catch (error) {
        onError(error)
      }
    }

    for (const annotation of store.allAnnotations()) {
      if (annotation.uid !== uid) continue
      const stamp = pushStamp(annotation.updatedAt, readers)
      if (pushed.get(annotation.concertId) === stamp) continue
      pushed.set(annotation.concertId, stamp)
      try {
        await remote.upsert(annotation, readers)
      } catch (error) {
        pushed.delete(annotation.concertId)
        onError(error)
      }
    }
  }

  const ready = pushLocal()

  const unsubscribeLocal = store.subscribe(() => {
    if (stopped || applyingRemote) return
    void pushLocal()
  })

  const unsubscribeOwn = remote.subscribeOwn(uid, (list) => {
    if (stopped) return
    applyingRemote = true
    try {
      for (const row of list) {
        // Stamped with the readers the cloud *has*, not the ones this device would
        // publish. Those differ exactly when a row was written before the friendship
        // existed — and stamping from intent there makes the next push skip it, so the
        // grant stays narrow and the friend never sees the note. Nothing fails.
        pushed.set(row.annotation.concertId, pushStamp(row.annotation.updatedAt, row.readers))
        store.applyRemote(row.annotation)
      }
    } finally {
      applyingRemote = false
    }

    /*
     * The re-entrancy guard above suppresses the store's own notify, which is what
     * normally drives a push — so a row that arrived holding a narrower grant than this
     * device would publish would sit there forever. Unconditional and safe: `pushLocal`
     * skips anything whose stamp already matches, and the echo of whatever it does
     * write stamps with the wider set, so the second pass finds nothing to do.
     */
    void pushLocal()
  })

  const unsubscribeShared = remote.subscribeShared(uid, (list) => {
    if (stopped) return
    applyingRemote = true
    try {
      // Its own source: this listener owns the `sharedWith` set and nothing else, so a
      // snapshot from it can never look like the friend feed's rows disappearing (KTD2).
      store.replaceSharedFrom('shared', list)
    } finally {
      applyingRemote = false
    }
  })

  const unsubscribeVisible = remote.subscribeVisible(uid, (list) => {
    if (stopped) return
    applyingRemote = true
    try {
      // Its own source, and pointedly not `applyRemote`: that merges into `items`, so a
      // friend's row would overwrite the reader's own note for the same concert on this
      // device — and `pushLocal`'s `uid !== uid` skip would then stop the wreckage from
      // ever reaching the cloud, leaving the note destroyed locally and nowhere else.
      store.replaceSharedFrom('visible', list)
    } finally {
      applyingRemote = false
    }
  })

  const unsubscribeFriends = remote.subscribeFriendList(uid, (list) => {
    if (stopped) return
    // A self-friendship is skipped rather than stored: the server should never write
    // one, and an entry naming the reader would put their own notes on the schedule as
    // a friend's and spend one of the 26 `readers` slots on nobody.
    friends.replaceFriends(list.filter((friend) => friend.uid !== uid))
    // The friend store has its own subscribers; this one is not among them, and the
    // annotation store — which is — has not changed. Re-publish explicitly: every
    // annotation written before this snapshot landed carries a narrower `readers` than
    // the user now has friends, and a narrow write is accepted, so nothing else would
    // ever notice.
    void pushLocal()
  })

  return {
    ready,
    stop() {
      stopped = true
      unsubscribeLocal()
      unsubscribeOwn()
      unsubscribeShared()
      unsubscribeVisible()
      unsubscribeFriends()
    },
  }
}
